import each from 'lodash/each';
import find from 'lodash/find';
import isArray from 'lodash/isArray';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import reduce from 'lodash/reduce';
import uniq from 'lodash/uniq';
import AllCarriers from '../carriers/AllCarriers';
import AllDescriptionTemplates from './AllDescriptionTemplates';
import AllMappedETOEServices from './AllMappedETOEServices';
import AllServices from './AllServices';
import { BlockedServicesConfig } from './BlockedServicesConfig';
import PackageType from './PackageType.enum';
import ServiceType from './ServiceType.enum';
import ShipmentContents from './ShipmentContents.enum';

export default class ServiceUtil {
	/**
	 * Data returned from carrier-services API call stored in
	 * [key: ServiceType]:[value: object] pair. When available,
	 * We use the data to build the dynamic service description
	 * for all services.
	 *
	 * For more information about carrier-services api spec,
	 * https://stamps.atlassian.net/browse/WEBAPPS-30894
	 */
	static _shippingOptionsLookup = {};

	/**
	 * Build the shipping-options lookup object. This requires the caller
	 * to make a carrier-services API cal to get the data to  initialize
	 * the class.
	 */
	static init = shippingOptions => {
		/**
		 * Loop through shippingOptions array, [services] > [packagingOptions] > [contentOptions],
		 * to normalize data structure and field names for better objects merging.
		 * The final output should look like this:
		 * [
		 * 	{
		 * 		[ServiceType]: {
		 * 			carrier,
		 * 			serviceType,
		 * 			serviceName,
		 * 			packages: {
		 * 				[PackageType]: {
		 * 					packageType,
		 * 					packageName,
		 * 					contentOptions: {
		 * 						[shipmentContents]: {
		 * 							maxMeasurements: { ... },
		 * 							...
		 * 						}
		 * 					}
		 * 				},
		 * 				...
		 * 			}
		 * 		}
		 * 	},
		 *  ...
		 * ]
		 */
		if (isArray(shippingOptions)) {
			this._shippingOptionsLookup = reduce(
				shippingOptions,
				(_shippingOptions, { carrier, services }) => {
					const _moreShippingOptions = reduce(
						services,
						(_allServices, { serviceType, serviceName, packagingOptions }) => ({
							..._allServices,
							[serviceType]: {
								carrier,
								serviceType,
								serviceName,
								packages: packagingOptions.reduce(
									(
										_allPackages,
										{ packagingType, packagingName, contentOptions }
									) => ({
										..._allPackages,
										[packagingType]: {
											packageType: packagingType,
											packageName: packagingName,
											contentOptions: contentOptions.reduce(
												(_contentOptions, contentOption) => ({
													..._contentOptions,
													[contentOption.shipmentContents]:
														contentOption.rulesAndOptions[0],
												}),
												{}
											),
										},
									}),
									{}
								),
							},
						}),
						{}
					);

					return {
						..._shippingOptions,
						..._moreShippingOptions,
					};
				},
				{}
			);
		}

		return this._shippingOptionsLookup;
	};

	/**
	 * Build all services array using AllServices static data and carrier-services data.
	 * @param {boolean} postalOverrideAppCap `true` to use AllMappedETOEServices data.
	 * @returns {array}
	 */
	static buildAllServices = postalOverrideAppCap => {
		const allServices = AllServices.reduce((_allServices, serviceData) => {
			const { carrier, packages, serviceType } = serviceData;
			const carrierDisplayName = find(AllCarriers, [
				'value',
				carrier,
			]).displayName;

			// check for service's postal override based on user's app cap
			const postalServiceOverride = this.findPostalServiceOverride(
				postalOverrideAppCap,
				serviceType
			);

			// check for service's options from the carrier-service lookup data, which is
			// used for dynamic content
			const serviceShippingOption = this.findShippingOptionsData(serviceType);

			// calculate the service name
			const serviceName = this.buildName(
				'serviceName',
				postalServiceOverride,
				serviceShippingOption,
				serviceData
			);

			// calculate the service description with the new service name
			const serviceTypeDescription = this.buildDescription(
				'serviceTypeDescription',
				postalServiceOverride,
				{ ...serviceData, serviceName }
			);

			// build available packages for the service type
			const moreServices = packages.map(packageData => {
				const { packageType } = packageData;

				// check for package's override based on user's app cap
				const postalPackageOverride = this.findPostalServiceOverride(
					postalOverrideAppCap,
					serviceType,
					packageType
				);

				// check for package's options from the carrier-service lookup data
				const packageShippingOption = this.findShippingOptionsData(
					serviceType,
					packageType
				);

				// merge service and package data
				const servicePackageData = {
					...serviceData,
					...packageData,

					// include data from carrier-services API
					packageShippingOptions: packageShippingOption.contentOptions,
				};

				// calculate package name
				servicePackageData.packageName = this.buildName(
					'packageName',
					postalPackageOverride,
					packageShippingOption,
					packageData
				);

				// calculate pacakge description
				servicePackageData.packageDescription = this.buildDescription(
					'packageDescription',
					postalPackageOverride,
					servicePackageData
				);

				// calculate package requirements
				servicePackageData.packageRequirements = this.buildPackageRequirements(
					postalPackageOverride,
					packageShippingOption.contentOptions,
					servicePackageData,
					serviceType
				);

				// merge service data and package data
				return {
					key: servicePackageData.key,
					value: servicePackageData.value,
					carrier,
					carrierDisplayName,

					// service information
					serviceType,
					serviceName,
					serviceTypeDescription,

					// package information
					packageType,
					packageName: servicePackageData.packageName,
					packageDescription: servicePackageData.packageDescription,
					packageRequirements: servicePackageData.packageRequirements,

					// data from carrier-services API
					packageShippingOptions: servicePackageData.packageShippingOptions,

					/**
					 * Per PPTWA-393, UPS Ground requires an API call to fetch the exact ETA.
					 * While fetching, system should display a spinner. To show the spinner,
					 * set this flag to `true`.
					 * See https://stamps.atlassian.net/browse/PPTWA-393.
					 */
					isFetching: false,
				};
			});

			return [..._allServices, ...moreServices];
		}, []);

		return allServices;
	};

	/**
	 * Return the service/package name with the highest priority. The order is as follows:
	 * 1) use the serviceName/packageName in AllMappedETOEServices if user's app cap is true.
	 * 2) use the serviceName/packageName in _shippingOptionsLookup (carrier-services data from server).
	 * 3) use the serviceName/packageName in AllServices as a fail-safe.
	 *
	 * @param {string} fieldName 'serviceName' or 'packageName'.
	 * @param {object} postalServiceOverride Postal service-package override object.
	 * @param {object} serviceShippingOption Service-package data object from carrier-services API.
	 * @param {object} staticData Service-package data object in AllServices.
	 * @returns {string}
	 */
	static buildName = (
		fieldName,
		postalServiceOverride,
		serviceShippingOption,
		staticData
	) =>
		find(
			[
				// override content
				postalServiceOverride[fieldName],

				// dynamic content
				isString(serviceShippingOption[fieldName])
					? serviceShippingOption[fieldName].replace('®', '<sup>&reg;</sup>')
					: null,

				// static content
				staticData[fieldName],
			],
			name => isString(name)
		);

	/**
	 * Return the service (or package) description with the highest priority. The order is as follows:
	 * 1) use the serviceTypeDescription|packageDescription in AllMappedETOEServices if user's app cap is true.
	 * 2) use the service|package description template from Product's spec.
	 * 3) use the serviceTypeDescription|packageDescription in AllServices as a fail-safe.
	 *
	 * @param {string} fieldName 'serviceTypeDescription' or 'packageDescription'.
	 * @param {object} postalServiceOverride Postal service-pacakge override object.
	 * @param {object} staticData Service-package data object in AllServices.
	 * @returns {string}
	 */

	static buildDescription = (fieldName, postalServiceOverride, staticData) =>
		find(
			[
				postalServiceOverride[fieldName],

				// dynamic description
				this.findDescriptionTemplate(fieldName, staticData),
			],
			desc => isString(desc)
		);

	/**
	 * Return the package requirements with the highest priority. The order is as follows:
	 * 1) use the packageRequirements in AllMappedETOEServices if user's app cap is true.
	 * 2) use the carrier-services data to build packageRequirements.
	 * 3) use the packageRequirements in AllServices as a fail-safe.
	 *
	 * @param {object} postalServiceOverride Postal service-pacakge override object.
	 * @param {object} packageShippingOption contentOptions in _shippingOptionsLookup object.
	 * @param {object} staticData Service-package object in AllServices.
	 * @param {string} serviceType The service type.
	 * @returns {array|null}
	 */
	static buildPackageRequirements = (
		postalServiceOverride,
		packageShippingOption,
		staticData,
		serviceType
	) => {
		const packageRequirements = find(
			[
				postalServiceOverride.packageRequirements,
				this.getPackageRequirements(packageShippingOption, serviceType),
				staticData.packageRequirements,
			],
			item => isArray(item) && item.length > 0
		);

		return packageRequirements;
	};

	/**
	 * Convert the max weight into lbs. and oz.
	 *
	 * @param {number} maxWeight
	 * @returns {string}
	 */
	static buildMaxWeightText = maxWeight => {
		/**
		 * Dev note:
		 * Use string type of decimal number for concatenating oz. string. This
		 * prevents the issue like 1 + 0.59 = 1.5899999999999999. Also, toFixed
		 * method is not preferred here as we don't know the number of decimals
		 * place.
		 */
		const decimal = Number.isInteger(maxWeight)
			? ''
			: maxWeight.toString().split('.')[1];
		const wholeNumber = Math.floor(maxWeight);

		if (maxWeight < 16) {
			return `${maxWeight} oz.`;
		} else if (maxWeight % 16 === 0) {
			return `${maxWeight / 16} lbs.`;
		}

		return decimal !== ''
			? `${Math.floor(wholeNumber / 16)} lbs. ${wholeNumber % 16}.${decimal} oz.`
			: `${Math.floor(maxWeight / 16)} lbs. ${maxWeight % 16} oz.`;
	};

	/**
	 * Return the service override object from AllMappedETOEServices object when
	 * 'PostalOverride' app cap is turned on.
	 *
	 * @param {boolean} postalOverrideAppCap 'PostalOverride' app cap.
	 * @param {ServiceType} serviceType Service type.
	 * @param {PackageType} packageType Package type.
	 * @returns {object}
	 */
	static findPostalServiceOverride = (
		postalOverrideAppCap,
		serviceType,
		packageType
	) => {
		const postalServiceOverride =
			postalOverrideAppCap === true &&
			isObject(AllMappedETOEServices[serviceType])
				? AllMappedETOEServices[serviceType]
				: {};

		// check for package's override based on user's app cap
		if (isString(packageType)) {
			return isObject(postalServiceOverride.packages?.[packageType])
				? postalServiceOverride.packages[packageType]
				: {};
		} else {
			return postalServiceOverride;
		}
	};

	/**
	 * Return the description template from AllDescriptionTemplates object.
	 * Returns `null` when the template is not found.
	 *
	 * @param {string} key 'serviceTypeDescription' or 'packageDescription'.
	 * @param {object} staticData Service-package object in AllServices.
	 * @returns {string|null}
	 */
	static findDescriptionTemplate = (key, staticData) => {
		const { serviceType, serviceName, packageType } = staticData;

		// check for service's template from Products requirement
		const serviceTemplate = isObject(AllDescriptionTemplates[serviceType])
			? AllDescriptionTemplates[serviceType]
			: {};

		const template = null;

		if (key === 'serviceTypeDescription') {
			const serviceMaxWeightText = this.getServiceMaxWeight(serviceType);

			return isString(serviceTemplate.serviceTypeDescription)
				? serviceTemplate.serviceTypeDescription
						.replace('[[serviceName]]', serviceName)
						.replace('[[maxWeight]]', serviceMaxWeightText)
				: template;
		} else if (key === 'packageDescription') {
			const packageTemplate = isObject(serviceTemplate.packages?.[packageType])
				? serviceTemplate.packages[packageType]
				: {};

			if (
				serviceType === ServiceType.UspsFirstClassMailIntl &&
				packageType === PackageType.LargeEnvelope
			) {
				/**
				 * first <max_weight> is associated with <shipment_contents> = "non_documents"
				 * second <max_weight> is associated with <shipment_contents> = "documents"
				 */
				const nonDocMaxWeightText = this.getPackageMaxWeight(
					serviceType,
					packageType,
					ShipmentContents.NonDocuments
				);
				const docMaxWeightText = this.getPackageMaxWeight(
					serviceType,
					packageType,
					ShipmentContents.Documents
				);

				return isString(packageTemplate.packageDescription)
					? packageTemplate.packageDescription
							.replace('[[maxWeight]]', nonDocMaxWeightText)
							.replace('[[maxWeight]]', docMaxWeightText)
					: template;
			} else {
				const allMaxWeightText = this.getPackageMaxWeight(
					serviceType,
					packageType
				);

				return isString(packageTemplate.packageDescription)
					? packageTemplate.packageDescription.replace(
							'[[maxWeight]]',
							allMaxWeightText
						)
					: template;
			}
		}

		return template;
	};

	/**
	 * Return service's options data from the carrier-service API.
	 *
	 * @param {ServiceType} serviceType Service type.
	 * @param {PackageType} packageType Package type.
	 * @returns {object}
	 */
	static findShippingOptionsData = (serviceType, packageType) => {
		const serviceShippingOption = isObject(
			this._shippingOptionsLookup[serviceType]
		)
			? this._shippingOptionsLookup[serviceType]
			: {};

		if (isString(packageType)) {
			return isObject(serviceShippingOption.packages?.[packageType])
				? serviceShippingOption.packages[packageType]
				: {};
		} else {
			return serviceShippingOption;
		}
	};

	/**
	 * Get the max weight of the service from _shippingOptionsLookup object.
	 *
	 * @param {ServiceType} serviceType Service type.
	 * @returns {string} "{x} lbs. {x} oz"
	 */
	static getServiceMaxWeight = serviceType => {
		const shippingOptions = this.findShippingOptionsData(serviceType);
		const maxWeight = reduce(
			shippingOptions.packages,
			(_maxWeight, { contentOptions }) => {
				let packagingMaxWeight = 0;

				each(Object.values(contentOptions), ({ maxMeasurements }) => {
					if (isNumber(maxMeasurements?.maxWeight)) {
						packagingMaxWeight =
							maxMeasurements.maxWeight > packagingMaxWeight
								? maxMeasurements.maxWeight
								: packagingMaxWeight;
					}
				});

				return packagingMaxWeight > _maxWeight
					? packagingMaxWeight
					: _maxWeight;
			},
			0
		);

		return maxWeight > 0 ? this.buildMaxWeightText(maxWeight) : '';
	};

	/**
	 * Get the max weight of the service-package from _shippingOptionsLookup object.
	 *
	 * @param {ServiceType} serviceType See ServiceType enum.
	 * @param {PackageType} packageType See PackageType enum.
	 * @param {ShipmentContents} shipmentContents See ShipmentContents enum.
	 * @returns {string} "{x} lbs. {x} oz"
	 */
	static getPackageMaxWeight = (serviceType, packageType, shipmentContents) => {
		// get the carrier-service lookup data
		const { contentOptions = {} } = this.findShippingOptionsData(
			serviceType,
			packageType
		);
		let maxWeight = 0;

		if (shipmentContents) {
			// look for the max weight based on the shipmentContents type
			maxWeight = isNumber(
				contentOptions[shipmentContents]?.maxMeasurements?.maxWeight
			)
				? contentOptions[shipmentContents].maxMeasurements.maxWeight
				: 0;
		} else {
			// look for the max weight of all possible shipmentContents types
			maxWeight = reduce(
				contentOptions,
				(_maxWeight, option) =>
					isNumber(option.maxMeasurements?.maxWeight) &&
					option.maxMeasurements.maxWeight > _maxWeight
						? option.maxMeasurements.maxWeight
						: _maxWeight,
				0
			);
		}

		return maxWeight > 0 ? this.buildMaxWeightText(maxWeight) : '';
	};

	/**
	 * Return the array of dimensional requirements for the service package, similar to
	 * `packageRequirements` array in AllServices.
	 * @param {object} contentOptions The content options data from carrier-services API.
	 * @param {string} serviceType The serviceType from AllServices static data.
	 * @returns {array}
	 */
	static getPackageRequirements = (contentOptions, serviceType) => {
		const shipmentContents = isObject(contentOptions)
			? Object.values(contentOptions)
			: null;

		return uniq(
			reduce(
				shipmentContents,
				(_packageRequirements, { maxMeasurements }) => {
					const {
						maxLength,
						maxWidth,
						maxHeight,
						maxLengthPlusGirth,
						maxSumOfDimensions,
					} = maxMeasurements;
					const maxDimensions = [maxLength, maxWidth, maxHeight]
						.filter(num => isNumber(num))
						.sort((n1, n2) => n2 - n1);

					if (maxDimensions.length === 3) {
						_packageRequirements.push(
							`Maximum dimensions are ${maxLength}" x ${maxWidth}" x ${maxHeight}".`
						);
					} else if (maxDimensions.length === 2) {
						_packageRequirements.push(
							`Largest side cannot exceed ${maxDimensions[0]}". Second largest side cannot exceed ${maxDimensions[1]}".`
						);
					} else if (maxDimensions.length === 1) {
						_packageRequirements.push(
							`Length, width, or height cannot exceed ${maxDimensions[0]}".`
						);
					}

					if (isNumber(maxLengthPlusGirth)) {
						_packageRequirements.push(
							`Length + 2x width + 2x height cannot exceed ${maxLengthPlusGirth}".`
						);
					}

					if (isNumber(maxSumOfDimensions)) {
						_packageRequirements.push(
							`Length + width + height cannot exceed ${maxSumOfDimensions}".`
						);
					}

					// WEBAPPS-36791: UPSGDS package requirements need to be added client side
					if (serviceType === ServiceType.UpsGroundSaver) {
						_packageRequirements.unshift(
							'Minimum dimensions: 6" x 4" x 0.75".'
						);
						_packageRequirements.push(
							'Dimensional weight cannot exceed 10 lb. The dimensional weight formula is L x W x H / 166.'
						);
					}

					return _packageRequirements;
				},
				[]
			)
		);
	};

	/**
	 * Filter services based on account capabilities using BlockedServicesConfig.
	 * @param {array} allServices All available services
	 * @param {array} carrierBlockRule Array of carrier block rules to check
	 * @returns {array} Filtered services
	 */
	static filterBlockedServices = (
		allServices,
		carrierBlockRules = ['fedexReseller']
	) => {
		// Collect all blocked service types based on carriers
		const blockedServiceTypes = new Set();

		carrierBlockRules.forEach(blockRule => {
			const carrierConfig = BlockedServicesConfig[blockRule];
			if (carrierConfig && carrierConfig.shouldBlock()) {
				carrierConfig.serviceTypes.forEach(serviceType => {
					blockedServiceTypes.add(serviceType);
				});
			}
		});

		// If no services are blocked, return all services
		if (blockedServiceTypes.size === 0) {
			return allServices;
		}

		// Filter out blocked services
		return allServices.filter(({ serviceType, service_type }) => {
			return !blockedServiceTypes.has(serviceType || service_type);
		});
	};
}
